<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { type Client, type UploadDriver, clientsApi, uploadDriversApi } from '@/api/entities'

definePage({
  meta: {
    action: 'read',
    subject: 'Client',
  },
})

// 상태
const page = ref(1)
const itemsPerPage = ref(15)
const totalItems = ref(0)
const searchQuery = ref('')
const loading = ref(false)
const clients = ref<Client[]>([])

// 필터
const uploadDrivers = ref<UploadDriver[]>([])
const selectedUploadDrivers = ref<UploadDriver[]>([])
const selectedType = ref<string | null>(null)

const types = [
  { title: '위젯', value: 'widget' },
  { title: '이관', value: 'migration' },
]

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id', sortable: true },
  { title: '업체명', key: 'name', sortable: true },
  { title: '유형', key: 'type', sortable: true },
  { title: 'DB명', key: 'target_database_hive', sortable: true },
  { title: 'PC 도메인', key: 'host', sortable: true },
  { title: '리뷰 솔루션', key: 'review_service', sortable: false },
  { title: '별점 기준', key: 'score', sortable: true },
  { title: '텍스트 리뷰', key: 'text', sortable: false },
  { title: '포토 리뷰', key: 'photo', sortable: false },
]

// 데이터 로드
const loadClients = async () => {
  loading.value = true
  try {
    const response = await clientsApi.listClients({
      page: page.value,
      limit: itemsPerPage.value,
      search: searchQuery.value || undefined,
    })

    if (response.success && response.data) {
      const data = response.data as any

      clients.value = data.data || data
      totalItems.value = data.total || clients.value.length
    }
  }
  catch (error) {
    console.error('고객사 목록 로드 실패:', error)
  }
  finally {
    loading.value = false
  }
}

const loadUploadDrivers = async () => {
  try {
    const response = await uploadDriversApi.listUploadDrivers({ limit: 100 })
    if (response.success && response.data) {
      const data = response.data as any

      uploadDrivers.value = data.data || data
    }
  }
  catch (error) {
    console.error('업로드 드라이버 목록 로드 실패:', error)
  }
}

// 검색 디바운스
const debouncedSearch = useDebounceFn(() => {
  page.value = 1
  loadClients()
}, 500)

// 감시자
watch([page, itemsPerPage, selectedUploadDrivers, selectedType], () => {
  loadClients()
})

watch(searchQuery, () => {
  debouncedSearch()
})

// 초기화
onMounted(() => {
  loadClients()
  loadUploadDrivers()
})

// 유형 배지 색상
const getTypeBadgeColor = (type: string) => {
  return type === 'widget' ? 'success' : 'info'
}

const getTypeLabel = (type: string) => {
  return type === 'widget' ? '위젯' : '이관'
}
</script>

<template>
  <VCard>
    <VCardTitle class="d-flex align-center justify-space-between flex-wrap gap-4 pa-6">
      <span>고객사 관리</span>
      <!-- 신규 고객사 등록 버튼 - 추후 구현 예정 -->
    </VCardTitle>

    <VDivider />

    <VCardText>
      <VRow class="mb-4">
        <VCol
          cols="12"
          md="3"
        >
          <AppTextField
            v-model="searchQuery"
            placeholder="검색어 입력"
            prepend-inner-icon="tabler-search"
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="3"
        >
          <AppSelect
            v-model="selectedUploadDrivers"
            :items="uploadDrivers"
            item-title="driver_name"
            item-value="id"
            placeholder="리뷰 서비스"
            multiple
            chips
            closable-chips
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="2"
        >
          <AppSelect
            v-model="selectedType"
            :items="types"
            placeholder="유형"
            clearable
          />
        </VCol>
      </VRow>

      <VDataTableServer
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="clients"
        :items-length="totalItems"
        :loading="loading"
        class="text-no-wrap"
      >
        <!-- 업체명 -->
        <template #item.name="{ item }">
          <RouterLink
            :to="{ name: 'review-client-id', params: { id: item.id } }"
            class="text-primary font-weight-medium"
          >
            {{ item.name }}
          </RouterLink>
        </template>

        <!-- 유형 -->
        <template #item.type="{ item }">
          <VChip
            :color="getTypeBadgeColor(item.type)"
            size="small"
          >
            {{ getTypeLabel(item.type) }}
          </VChip>
        </template>

        <!-- DB명 -->
        <template #item.target_database_hive="{ item }">
          <VChip
            color="secondary"
            size="small"
          >
            {{ item.target_database_hive }}
          </VChip>
        </template>

        <!-- 리뷰 솔루션 -->
        <template #item.review_service="{ item }">
          {{ item.upload_driver?.driver_name || '' }}
        </template>

        <!-- 별점 기준 -->
        <template #item.score="{ item }">
          <VRating
            :model-value="item.score"
            readonly
            density="compact"
            size="small"
          />
        </template>

        <!-- 텍스트 리뷰 -->
        <template #item.text="{ item }">
          <VIcon
            :icon="item.text === 'Y' ? 'tabler-check' : 'tabler-x'"
            :color="item.text === 'Y' ? 'success' : 'error'"
          />
        </template>

        <!-- 포토 리뷰 -->
        <template #item.photo="{ item }">
          <VIcon
            :icon="item.photo === 'Y' ? 'tabler-check' : 'tabler-x'"
            :color="item.photo === 'Y' ? 'success' : 'error'"
          />
        </template>
      </VDataTableServer>
    </VCardText>
  </VCard>
</template>
